/* Eski yazı gövdesini (`body_html`) blok belgesine çevirir — editörde ilk açılışta.

   Yazılar veritabanında elle yazılmış HTML olarak duruyor (data/posts/*.html
   tohumundan). Blokları olmayan bir yazı editörde açıldığında gövde
   tarayıcının kendi ayrıştırıcısıyla (`DOMParser`) okunuyor:

     h2–h4        başlık
     p            paragraf
     ul / ol      liste (iç içe dahil)
     blockquote   alıntı
     table        tablo
     hr           ayırıcı

   Tanınmayan her parça (`figure`, gömülü video…) `html` bloğu olarak
   olduğu gibi kalıyor: hiçbir içerik kaybolmuyor, yönetici onu silebilir
   ya da yerinde bırakabilir. Metinler şemadan geçerken temizleniyor
   (blocks.ts), yani buradan da belgeye ham HTML girmiyor. */

import { newId, sanitizeRich, type Block, type ListItem, type Rich } from '@frawgraphy/shared';

const rich = (html: string): Rich => ({ tr: sanitizeRich(html.replace(/\s+/g, ' ').trim()) });

export function htmlToBlocks(html: string): Block[] {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const taken = new Set<string>();
  const id = (prefix: string) => {
    const v = newId(prefix, taken);
    taken.add(v);
    return v;
  };

  const listItems = (list: Element): ListItem[] =>
    [...list.children]
      .filter((li) => li.tagName === 'LI')
      .map((li) => {
        const nested = [...li.children].find((c) => c.tagName === 'UL' || c.tagName === 'OL');
        const copy = li.cloneNode(true) as Element;
        copy.querySelectorAll('ul, ol').forEach((n) => n.remove());
        const item: ListItem = { text: rich(copy.innerHTML) };
        if (nested) item.items = listItems(nested);
        return item;
      });

  const blocks: Block[] = [];
  for (const node of [...doc.body.childNodes]) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) blocks.push({ id: id('paragraf'), type: 'paragraph', text: rich(text) });
      continue;
    }
    if (!(node instanceof Element)) continue;
    const tag = node.tagName;
    if (tag === 'H2' || tag === 'H3' || tag === 'H4') {
      blocks.push({ id: id('baslik'), type: 'heading', level: Number(tag[1]) as 2 | 3 | 4, text: rich(node.innerHTML) });
    } else if (tag === 'P') {
      if (node.textContent?.trim()) blocks.push({ id: id('paragraf'), type: 'paragraph', text: rich(node.innerHTML) });
    } else if (tag === 'UL' || tag === 'OL') {
      const items = listItems(node);
      if (items.length) blocks.push({ id: id('liste'), type: 'list', ordered: tag === 'OL', items });
    } else if (tag === 'BLOCKQUOTE') {
      blocks.push({ id: id('alinti'), type: 'quote', text: rich(node.innerHTML) });
    } else if (tag === 'HR') {
      blocks.push({ id: id('cizgi'), type: 'divider' });
    } else if (tag === 'TABLE') {
      const rows = [...node.querySelectorAll('tr')].map((tr) => [...tr.children].map((cell) => rich(cell.innerHTML)));
      const width = Math.max(1, ...rows.map((r) => r.length));
      const even = rows.map((r) => [...r, ...Array.from({ length: width - r.length }, () => ({ tr: '' }))]);
      if (even.length) blocks.push({ id: id('tablo'), type: 'table', header: Boolean(node.querySelector('th')), rows: even });
    } else {
      blocks.push({ id: id('eski'), type: 'html', html: node.outerHTML });
    }
  }
  return blocks;
}
